"use client";
import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { getCountries, getCountryCallingCode } from "libphonenumber-js";
import * as flags from "country-flag-icons/react/3x2";
import { FiX } from "react-icons/fi";
import { BsBank2 } from "react-icons/bs";
import { MdEmail, MdOutlinePlace } from "react-icons/md";
import { FaPhoneAlt, FaRegIdBadge } from "react-icons/fa";
import { IoBusinessOutline } from "react-icons/io5";
import { LuGlobe } from "react-icons/lu";
import toast from "react-hot-toast";
import { REST_API_URL } from "../../api/config";

const API_BASE = `${REST_API_URL}/invoice/bank-info/`;

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

const BankInfoModal = ({ onClose, onUpdated }) => {
  const [form, setForm] = useState({
    name_surname: "",
    company_name: "",
    tax_id: "",
    address: "",
    phone_number: "",
    email: "",
    iban: "",
    swift_bic: "",
  });

  const [loading, setLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("US");
  const [countryCode, setCountryCode] = useState("+1");
  const [openCountries, setOpenCountries] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchBankInfo = async () => {
      const token =
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken");

      if (!token) {
        toast.error("Authorization token not found");
        return;
      }

      try {
        setLoading(true);
        const headers = buildHeaders(token);
        
        const res = await fetch(API_BASE, {
          headers,
        });

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          console.error("❌ Fetch error:", errData);
          toast.error(`Error ${res.status}: Failed to load bank info`);
          return;
        }

        const data = await res.json();
        setForm(data || {});
      } catch (err) {
        console.error("❌ Failed to load bank info:", err);
        toast.error("Network error while loading bank info");
      } finally {
        setLoading(false);
      }
    };

    fetchBankInfo();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenCountries(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));


  const handleSave = async () => {
    const token =
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken");

    if (!token) {
      toast.error("Authorization token not found");
      return;
    }

    try {
      setLoading(true);

      const rawPhone = (form.phone_number || "").trim();

      let fullPhone = "";
      if (rawPhone) {
        if (rawPhone.startsWith("+")) {
          fullPhone = rawPhone.replace(/\s+/g, "");
        } else {
          const clean = rawPhone.replace(/[^\d]/g, "");
          fullPhone = clean ? `${countryCode}${clean}` : "";
        }
      }

      const payload = {
        name_surname: form.name_surname?.trim() || "",
        company_name: form.company_name?.trim() || "",
        tax_id: form.tax_id?.trim() || "",
        address: form.address?.trim() || "",
        phone_number: fullPhone,
        email: form.email?.trim() || "",
        iban: form.iban?.trim() || "",
        swift_bic: form.swift_bic?.trim() || "",
      };

      const headers = buildHeaders(token);
      
      const res = await fetch(API_BASE, {
        method: "PUT",
        headers,
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        console.error("❌ Error:", errData);

        if (errData && typeof errData === "object") {
          const messages = [];
          Object.values(errData).forEach((val) => {
            if (Array.isArray(val)) messages.push(...val);
            else if (typeof val === "string") messages.push(val);
          });
          if (messages.length > 0) {
            messages.forEach((msg) => toast.error(msg));
            return;
          }
        }

        toast.error(`Error ${res.status}: Failed to save bank info`);
        return;
      }

      toast.success("Bank info updated successfully!");

      if (onUpdated) onUpdated();

      onClose();

    } catch (err) {
      console.error("❌ Fetch failed:", err);
      toast.error("Network error — check console");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white border border-[#9A99AB33] shadow-md rounded-[16px] w-[750px] max-h-[90vh] flex flex-col justify-between p-6 relative overflow-y-auto"
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-[20px] font-bold text-[#302B2B]">Bank info</h2>
          <button
            onClick={onClose}
            className="w-6 h-6 flex items-center justify-center hover:bg-gray-100 rounded-full"
          >
            <FiX className="text-[#302B2B]" size={20} />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-10 text-gray-500">
            Loading...
          </div>
        ) : (
          <div className="space-y-4 overflow-y-auto pr-2">
            <div className="flex gap-4">
              <InputField
                label="Name Surname"
                icon={<FaRegIdBadge />}
                placeholder="Enter full name"
                value={form.name_surname}
                onChange={(v) => handleChange("name_surname", v)}
              />
              <InputField
                label="Company Name"
                icon={<IoBusinessOutline />}
                placeholder="Enter company name"
                value={form.company_name}
                onChange={(v) => handleChange("company_name", v)}
              />
            </div>

            <div className="flex gap-4">
              <InputField
                label="TAX ID / Company Registration"
                icon={<FaRegIdBadge />}
                placeholder="e.g. 101-202-303"
                value={form.tax_id}
                onChange={(v) => handleChange("tax_id", v)}
              />
              <InputField
                label="Address"
                icon={<MdOutlinePlace />}
                placeholder="Enter company address"
                value={form.address}
                onChange={(v) => handleChange("address", v)}
              />
            </div>

            <div className="flex gap-4">
              <div className="flex flex-col w-[338px]" ref={dropdownRef}>
                <label className="text-[14px] text-[#302B2B] mb-1">
                  Phone Number
                </label>
                <div className="border border-[#9A99AB] rounded-[14px] flex items-center gap-2 px-3 py-3">
                  <div
                    className="flex items-center gap-2 cursor-pointer bg-[#EDEEF3]/70 rounded-[14px] px-2 py-1"
                    onClick={() => setOpenCountries(!openCountries)}
                  >
                    {React.createElement(flags[selectedCountry] || flags.US, {
                      title: selectedCountry,
                      style: { width: 24, height: 18 },
                    })}
                    <span className="text-[#302B2B] text-[14px] font-medium">
                      {countryCode}
                    </span>
                  </div>
                  <input
                    type="tel"
                    placeholder="Enter phone number"
                    value={form.phone_number}
                    onChange={(e) =>
                      handleChange("phone_number", e.target.value)
                    }
                    className="flex-1 outline-none text-[16px] text-[#9A99AB]"
                  />
                </div>

                {openCountries && (
                  <div className="absolute mt-1 max-h-48 overflow-y-auto w-[180px] bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    {getCountries().map((c) => (
                      <div
                        key={c}
                        className="flex justify-between items-center px-3 py-2 hover:bg-gray-100 cursor-pointer"
                        onClick={() => {
                          setSelectedCountry(c);
                          setCountryCode("+" + getCountryCallingCode(c));
                          setOpenCountries(false);
                        }}
                      >
                        <div className="flex items-center gap-2">
                          {React.createElement(flags[c] || flags.US, {
                            title: c,
                            style: { width: 20, height: 14 },
                          })}
                          <span>{c}</span>
                        </div>
                        <span>+{getCountryCallingCode(c)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <InputField
                label="Email Address"
                icon={<MdEmail />}
                placeholder="Enter email address"
                value={form.email}
                onChange={(v) => handleChange("email", v)}
              />
            </div>

            {/* Row 4 */}
            <div className="flex gap-4">
              <InputField
                label="IBAN"
                icon={<BsBank2 />}
                placeholder="AB 12 ABCD 102030 1245678"
                value={form.iban}
                onChange={(v) => handleChange("iban", v)}
              />
              <InputField
                label="SWIFT / BIC"
                icon={<LuGlobe />}
                placeholder="LOYDGB2L"
                value={form.swift_bic}
                onChange={(v) => handleChange("swift_bic", v)}
              />
            </div>
          </div>
        )}

        <div className="flex justify-end items-center gap-4 mt-6">
          <button
            onClick={onClose}
            className="text-[#9A99AB] text-[16px] hover:text-[#302B2B]"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="bg-[#5563F6] text-white px-8 py-3 rounded-[14px] hover:opacity-90 font-medium disabled:opacity-50"
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const InputField = ({ label, icon, placeholder, value, onChange }) => (
  <div className="flex flex-col w-[338px]">
    <label className="text-[14px] text-[#302B2B] mb-1">{label}</label>
    <div className="border border-[#9A99AB] rounded-[14px] flex items-center gap-2 px-3 py-3">
      <span className="text-[#302B2B] opacity-70">{icon}</span>
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 outline-none text-[16px] text-[#9A99AB]"
      />
    </div>
  </div>
);

export default BankInfoModal;